import { createContext, useContext } from 'react';
import type { SearchResult } from '@backstage/plugin-search-common';

type SearchPageConfig = {
  title?: string;
  path?: string;
  noTrack?: boolean;
  [key: string]: unknown;
};

type ResultItemComponent = (props: {
  result: SearchResult['document'];
  rank?: number;
  noTrack?: boolean;
  [key: string]: unknown;
}) => JSX.Element | null;

type ResultType = {
  value: string;
  name: string;
  icon: JSX.Element;
};

type SearchFilterComponent = (props: { className: string }) => JSX.Element;

type SearchPageContextValue = {
  /** Page-level config from the search extension (e.g. noTrack, title). */
  config: SearchPageConfig;
  /** Resolves the correct SearchResultListItem component for a given search result based on registered predicates. */
  getResultItemComponent: (result: SearchResult) => ResultItemComponent;
  /** Registered result types with their display name and icon (from SearchFilterResultTypeBlueprint). */
  resultTypes: ResultType[];
  /** Global search filter components registered via SearchFilterBlueprint, shown on all tabs. */
  searchFilters: SearchFilterComponent[];
  /** Map of result type value to filter components, used to show type-specific filters per tab. */
  filtersByType: Record<string, (() => JSX.Element)[]>;
};

const SearchPageContext = createContext<SearchPageContextValue | undefined>(
  undefined,
);

export type {
  SearchPageConfig,
  ResultItemComponent,
  ResultType,
  SearchFilterComponent,
};

export const SearchPageContextProvider = SearchPageContext.Provider;

export const useSearchPageContext = (): SearchPageContextValue => {
  const context = useContext(SearchPageContext);
  if (!context) {
    throw new Error(
      'useSearchPageContext must be used within a SearchPageContextProvider',
    );
  }
  return context;
};
